import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/cn';

const alertVariants = cva(
  'relative w-full rounded-lg border bg-surface p-4 [&>svg]:absolute [&>svg]:top-4 [&>svg]:left-4 [&>svg]:text-foreground [&>svg+div]:-translate-y-0.75 [&>svg~*]:pl-7',
  {
    variants: {
      variant: {
        default: 'bg-surface text-foreground',
        info: 'border-info/10 bg-info-subtle text-info dark:border-info/10 dark:bg-info/20 dark:text-info-foreground [&>svg]:text-info dark:[&>svg]:text-info-foreground',
        warning:
          'border-warning/10 bg-warning-subtle text-warning dark:border-warning/10 dark:bg-warning/20 dark:text-warning-foreground [&>svg]:text-warning dark:[&>svg]:text-warning-foreground',
        muted:
          'border-border bg-subtle/40 text-foreground [&>svg]:text-muted-foreground',
        destructive:
          'border-destructive/10 bg-destructive-subtle text-destructive dark:border-destructive/10 dark:bg-destructive/20 dark:text-destructive-foreground [&>svg]:text-destructive dark:[&>svg]:text-destructive-foreground',
        success:
          'border-success/10 bg-success-subtle text-success dark:border-success/10 dark:bg-success/20 dark:text-success-foreground [&>svg]:text-success dark:[&>svg]:text-success-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

interface AlertProps
  extends React.ComponentProps<'div'>, VariantProps<typeof alertVariants> {}

function Alert({ className, variant, ref, ...props }: AlertProps) {
  return (
    <div
      ref={ref}
      role='alert'
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

type AlertTitleProps = React.ComponentProps<'h5'>;

function AlertTitle({ className, children, ref, ...props }: AlertTitleProps) {
  return (
    <h5
      ref={ref}
      className={cn('mb-1 leading-none font-medium tracking-tight', className)}
      {...props}
    >
      {children}
    </h5>
  );
}

type AlertDescriptionProps = React.ComponentProps<'div'>;

function AlertDescription({ className, ref, ...props }: AlertDescriptionProps) {
  return (
    <div
      ref={ref}
      className={cn('text-sm font-normal [&_p]:leading-relaxed', className)}
      {...props}
    />
  );
}

export { Alert, AlertDescription, AlertTitle, alertVariants };
